import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
  ViewContainerRef,
  inject,
  viewChild,
} from '@angular/core';
import { LayoutConfigService } from './core/services/layout-config.service';
import { ClassInitService } from './core/services/class-init.service';
import { TranslationService } from './core/services/translation.service';
import * as objectPath from 'object-path';
import { DomSanitizer, Title } from '@angular/platform-browser';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
  RouterOutlet,
  RouterState,
} from '@angular/router';
import { PageConfigService } from './core/services/page-config.service';
import { configureChunkRecovery, isChunkLoadError, reloadForChunkError } from './core/chunk-reload';
import { AppUpdateService } from './core/services/app-update.service';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { SplashScreenService } from './core/services/splash-screen.service';
import { AclService } from './core/services/acl.service';
import { Subject } from 'rxjs';
// language list
import { locale as enLang } from './config/i18n/en';
import { AuthenticationService } from './core/auth/authentication.service';
import {
  isNullOrEmptyString,
  isNullOrUndefined,
} from './content/pages/components/general/event/shared/validator/undefined-or-null';
import { TokenStorage } from './core/auth/token-storage.service';
import { ActivatedRoute } from '@angular/router';
import { DynamicScriptLoaderService } from './core/services/DynamicScriptLoaderService.service';
import { SpinnerOverlayService } from './core/services/spinner/spinner-overlay.service';
import { LazyLoadedRoutes } from './config/lazy-loaded-routes';
import { CommonService } from './core/services/common.service';
import { UserService } from './core/services/user.service';
import { SpinnerOverlayComponent } from './core/services/spinner/spinner-overlay.component';
import { ConfirmationOverlayComponent } from './core/services/confirmation-overlay/confirmation-overlay.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DashboardHttpService } from './content/pages/components/general/dashboard/dashboard-http/dashboard-http.service';
// LIST KNOWN ISSUES
// [Violation] Added non-passive event listener; https://github.com/angular/angular/issues/8866

@Component({
  selector: 'body[m-root]',
  templateUrl: './app.component.html',
  imports: [
    RouterOutlet,
    SpinnerOverlayComponent,
    ConfirmationOverlayComponent,
    MatProgressSpinner,
  ],
})
export class AppComponent implements AfterViewInit, OnInit, OnDestroy {
  private layoutConfigService = inject(LayoutConfigService);
  private classInitService = inject(ClassInitService);
  private sanitizer = inject(DomSanitizer);
  private translationService = inject(TranslationService);
  private router = inject(Router);
  private pageConfigService = inject(PageConfigService);
  private splashScreenService = inject(SplashScreenService);
  private aclService = inject(AclService);
  private authService = inject(AuthenticationService);
  private tokenStorage = inject(TokenStorage);
  private route = inject(ActivatedRoute);
  private dynamicScript = inject(DynamicScriptLoaderService);
  private spinnerOverlayService = inject(SpinnerOverlayService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private titleService = inject(Title);
  private commonService = inject(CommonService);
  private userService = inject(UserService);
  public vcRef = inject(ViewContainerRef);
  private dashboardService = inject(DashboardHttpService);
  private appUpdateService = inject(AppUpdateService);

  title = 'Metronic';

  @HostBinding('style') style: any;
  @HostBinding('class') classes: any = '';

  readonly splashScreen = viewChild('splashScreen', { read: ElementRef });
  splashScreenImage: string;
  private unsubscribe$ = new Subject<void>();

  constructor() {
    if (location.href.toLowerCase().includes('auth/confirm')) {
      this.tokenStorage.clear();
    }

    this.aclService.initialize();

    // Move this tab onto a new deploy at its next navigation (MP-5600), but
    // never while there are unsaved edits (isBlockRoute, the same flag
    // unsavedChangesGuard uses) or between creation wizard steps (create-event,
    // create-season-pass, create-class-pass), whose state lives only in the
    // component. Chunk errors outside a navigation don't reload over unsaved
    // edits either; they are only reported.
    configureChunkRecovery({ allowUnroutedRecovery: () => !this.commonService.isBlockRoute });
    this.appUpdateService.start({
      skipFullLoad: url =>
        this.commonService.isBlockRoute || /\/create-(event|season-pass|class-pass)(\/|\?|#|$)/.test(url),
    });

    // subscribe to class update event
    this.classInitService.onClassesUpdated$.subscribe((classes) => {
      // get body class array, join as string classes and pass to host binding class
      setTimeout(() => (this.classes = classes.body.join(' ')));
    });

    this.layoutConfigService.onLayoutConfigUpdated$.subscribe((model) => {
      this.classInitService.setConfig(model);

      this.style = '';
      if (objectPath.get(model.config, 'self.layout') === 'boxed') {
        const backgroundImage = objectPath.get(model.config, 'self.background');
        if (backgroundImage) {
          this.style = this.sanitizer.bypassSecurityTrustStyle(
            'background-image: url(' + objectPath.get(model.config, 'self.background') + ')'
          );
        }
      }

      // splash screen image
      this.splashScreenImage = objectPath.get(model.config, 'loader.image');
    });

    // register translations
    this.translationService.loadTranslations(enLang);

    // override config by router change from pages config
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        this.layoutConfigService.setModel(
          { page: objectPath.get(this.pageConfigService.getCurrentPageConfig(), 'config') },
          true
        );
      });

    // handles the loader start and end without any delay when the navigations start
    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((event) => {
        if (event && !this.commonService.isBlockRoute) {
          if (event instanceof NavigationStart && this.isExcludeFromTriggerLoading(event)) {
            this.loadRouterSpinner(event, true);
          } else if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
            const isAuthExists = event.url.includes('auth');
            this.loadRouterSpinner(event, false, isAuthExists);
            if (event instanceof NavigationEnd) {
              this.removeVersionChangeFromURL();
            }
          }
          if (event instanceof NavigationError) {
            // Always dismiss the route spinner. If reloadForChunkError ends up
            // navigating, the new page moots it anyway; if it doesn't (guard
            // suppressed within the 90s window, or non-chunk error), this
            // prevents the user being stuck on a "Processing..." spinner.
            this.loadRouterSpinner(event, false);
            if (isChunkLoadError(event.error)) {
              reloadForChunkError(event.error, event.url);
              this.changeDetectorRef.detectChanges();
            }
          }
          if (event instanceof NavigationEnd) this.setTitle();
        }
      });

    // Debounced observer for footer/sticky-bar state to handle transient sidebar reload navigations
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        debounceTime(200),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((event) => {
        this.checkEventUrlForFooter(event);
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private setTitle() {
    const titles = this.getTitle(this.router.routerState, this.router.routerState.root);
    if (titles && titles.length) {
      // Always append the global default title
      titles.unshift('SimpleTix Dashboard');
      const title = titles.join(' | ');
      this.titleService.setTitle(title);
    }
  }
  private getShowUrlLink(eventIdentifire: string) {
    this.commonService.previousShowIdentifier = eventIdentifire;
    this.dashboardService.getShowDetailUrl(eventIdentifire).subscribe({
      next: (res) => {
        if (res && res.data) {
          this.commonService.eventLinkForFooter.next(res.data);
          this.changeDetectorRef.detectChanges();
        }
      },
      error: (err) => {
        console.error('Error fetching show detail URL:', err);
        this.commonService.eventLinkForFooter.next({
          eventLinkURL: '',
          eventTitle: '',
          eventType: 0,
          showID: 0,
        });
      },
    });
  }

  // check event URL for show Event link in footer
  private checkEventUrlForFooter(event) {
    try {
      const userInfo = this.tokenStorage.getUserInfo();
      const allowedRoles = ['owner', 'admin', 'promoter', 'manager'];
      const hasAccess = allowedRoles.includes(userInfo?.['value']?.roleName?.toLowerCase() ?? '');

      if (!hasAccess) {
        return;
      }

      // Show Footer Event link in Event Pages
      const url = (event.urlAfterRedirects || '').split('?')[0];
      const urlParts = url.split('/');
      const isEventMenuPage = this.commonService.isMenuPage(url);
      const currentLink = this.commonService.eventLinkForFooter.value;

      // Check for event identifier (UUID)
      if (isEventMenuPage && urlParts) {
        const eventIdentifier = urlParts?.find((part) =>
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(part)
        );

        // Fetch link if ID changed OR if footer link was cleared by previous global navigation
        if (
          eventIdentifier &&
          (this.commonService.previousShowIdentifier !== eventIdentifier ||
            !currentLink?.eventLinkURL)
        ) {
          this.commonService.setStickyBarVisibility(true);
          this.getShowUrlLink(eventIdentifier);
        } else if (!eventIdentifier) {
          this.commonService.eventLinkForFooter.next({
            eventLinkURL: '',
            eventTitle: '',
            eventType: 0,
            showID: 0,
          });
          this.commonService.setStickyBarVisibility(false);
          this.commonService.previousShowIdentifier = '';
        } else {
          // Already have correct ID and Link, but ensure visibility is maintained
          this.commonService.setStickyBarVisibility(true);
        }
      } else {
        this.commonService.eventLinkForFooter.next({
          eventLinkURL: '',
          eventTitle: '',
          eventType: 0,
          showID: 0,
        });
        this.commonService.setStickyBarVisibility(false);
        this.commonService.previousShowIdentifier = '';
      }
    } catch (err) {
      this.commonService.eventLinkForFooter.next({
        eventLinkURL: '',
        eventTitle: '',
        eventType: 0,
        showID: 0,
      });
      console.error('Error in checkEventUrlForFooter', err);
      return;
    }
  }

  private getTitle(state: RouterState, parent: ActivatedRoute): string[] {
    try {
      const data = [];
      if (parent && parent.snapshot.data && parent.snapshot.data.title) {
        data.push(parent.snapshot.data.title);
      }
      if (state && parent) {
        data.push(...this.getTitle(state, parent.firstChild));
      }
      return data;
    } catch (_err) {
      return [];
    }
  }

  ngOnInit(): void {
    let token: string = this.tokenStorage.getAccessTokens();
    var applicationId = '';
    var domain = '';
    this.loadSquareJS();
    if (location.search.toLowerCase().includes('appid')) {
      this.route.queryParamMap.subscribe((queryParams) => {
        applicationId = queryParams['params']['AppId'];
      });
    } else if (location.search.toLowerCase().includes('domain')) {
      this.route.queryParams.subscribe((params) => {
        domain = params.domain;
        if (domain) {
          this.getUserRoleWebSites(domain);
        }
      });
    }
    if (
      !isNullOrUndefined(applicationId) &&
      !isNullOrEmptyString(applicationId) &&
      isNullOrUndefined(token)
    ) {
      this.authService.checkCurrentDomain(applicationId).subscribe((res) => {
        if (res.isSuccessful && !isNullOrUndefined(res.data.sslDomain)) {
          sessionStorage.setItem('sslDomain', res.data.sslDomain);
          if (
            location.pathname != '/auth' &&
            location.pathname != '/404' &&
            location.pathname != '/403'
          )
            this.tokenStorage.setPathName(location.pathname);
          else {
            localStorage.removeItem('pathName');
          }
          //this.authService.logout(true);
        } else {
          // localStorage.removeItem('pathName');
        }
        if (!res.data.isDomainPresent) {
          this.authService.logout(true);
        }
      });
    }
  }

  private getUserRoleWebSites(domain) {
    this.userService.getUserRoleWebsitesListByUser().subscribe((res) => {
      if (res.isSuccessful && res.data && res.data.length) {
        const website = res.data.find((x) => x.sslDomain.includes(domain));
        if (website && website.applicationId) {
          this.redirectUserToNewWebsite(website.applicationId);
        }
      }
    });
  }

  private removeVersionChangeFromURL(): void {
    const location = new URL(window.location.href);
    const params = new URLSearchParams(location.search);
    if (params.has('v')) {
      params.delete('v');
      location.search = params.toString();
      window.history.replaceState(window.history.state, document.title, location.toString());
    }
  }

  // used to start and end the spinning
  private loadRouterSpinner(
    event: RouterEvent,
    isLoadSpinner: boolean,
    isAuthExists: boolean = false
  ) {
    const lazyLoadedRoutes: string[] = LazyLoadedRoutes;
    if (
      event &&
      event.url &&
      lazyLoadedRoutes &&
      lazyLoadedRoutes.length > 0 &&
      this.compareUrl(lazyLoadedRoutes, event.url)
    ) {
      this.checkSpinningReq(isLoadSpinner);
      if (isAuthExists && this.spinnerOverlayService.spinningRequest) {
        this.spinnerOverlayService.stopSpinning();
      }
    }
  }

  private isExcludeFromTriggerLoading(event: NavigationStart): boolean {
    if (
      event &&
      event.navigationTrigger === 'popstate' &&
      event.url &&
      (event.url === '/create-event/step3' ||
        event.url === '/create-event/step2' ||
        event.url === '/create-event/step4' ||
        event.url === '/create-event/step5')
    ) {
      return false;
    }
    return true;
  }

  // used to start spinning and end spinning based on the condition. when the navigationStart and Navigation End Event
  private checkSpinningReq(isLoadSpinner: boolean): void {
    if (isLoadSpinner) {
      this.spinnerOverlayService.startSpinning();
    } else if (this.spinnerOverlayService.spinningRequest) {
      this.spinnerOverlayService.stopSpinning();
    }
  }

  // used to compare the array of routes with the eventURL which will return true or false.
  private compareUrl(routeConfig: string[], eventUrl: string): boolean {
    return routeConfig.some((route) => eventUrl.includes(route));
  }

  loadSquareJS() {
    try {
      if (location && location.href) {
        if (location.href.includes('localhost') || location.href.includes('dev')) {
          this.dynamicScript.load('square-sandbox');
        } else {
          this.dynamicScript.load('square');
        }
      }
    } catch (err) {
      console.error(err);
    }
  }

  redirectUserToNewWebsite(applicationId) {
    const requestModel = {
      signUpUrl: applicationId,
    };
    this.authService.switchSimpleTixSite(requestModel).subscribe((response) => {
      if (response.isSuccessful) {
        if (!isNullOrEmptyString(response.data.accessToken)) {
          this.setKeysLocalStorage(response);
          if (
            response.data.roles[0] == 'BOXOFFICE' ||
            response.data.roles[0] == 'SALESCHECKINORDEREDITING'
          ) {
            window.location.href = '/orders/sell-ticket';
            sessionStorage.removeItem('sslDomain');
          } else {
            window.location.href = '/';
            sessionStorage.removeItem('sslDomain');
          }
        }
        this.tokenStorage.setUserRoleAndAppId('8');
      }
    });
  }

  private setKeysLocalStorage(response: any) {
    localStorage.removeItem('keys');
    if (response.data.userInfo) {
      var keys = {
        currentOrganizationSignupLink: response.data.currentOrganizationSignupLink,
        isAnyActivePaymentGateWay: response.data.isAnyActivePaymentGateWay,
        domain: response.data.userInfo.domain,
        isDisplaySeatingChartSettings: response.data.userInfo.isDisplaySeatingChartSettings,
        isDisableCustomNotificationFeatures: response.data.isDisableCustomNotificationFeatures,
        isDisplayAweber: response.data.userInfo.isDisplayAweber,
        isAppInEU: response.data.userInfo.isAppInEU,
        enableCCPopup: response.data.userInfo.enableCCPopup,
        billingMode: response.data.userInfo.billingMode,
        advertiserName: response.data.userInfo.advertiserName,
        defaultCountry: response.data.userInfo.defaultCountry,
        enableAlvaradoIntegration: response.data.userInfo.enableAlvaradoIntegration,
        enableMyPassTracker: response.data.userInfo.enableMyPassTracker,
        isDisplayCustomCSSandTrackingMenu: response.data.userInfo.isDisplayCustomCSSandTrackingMenu,
      };
      localStorage.setItem('keys', JSON.stringify(keys));
    }
  }

  ngAfterViewInit(): void {
    if (this.splashScreen()) {
      this.splashScreenService.init(this.splashScreen().nativeElement);
    }
  }
}
